import { IDecisionMenuConfig } from './article-workflow';
import { ECheckType } from './check-result';
import { IReviewerRecommendationProvider } from './reviewer-recommendation';
import { IAuthorRequirementOptions } from './submission';
import { EUADCategory, EUADCheckType, EUADRisk } from './uad';
import { IRoleType } from './workflow-phase-ownership';

export const SubmissionContextTypes = ['journal', 'special_issue'] as const;
export type ISubmissionContextType = (typeof SubmissionContextTypes)[number];

export const journalPublishingModels = {
  subscription: 'Subscription',
  openAccess: 'Open Access',
  hybrid: 'Hybrid'
};

export type IJournalPublishingModel = keyof typeof journalPublishingModels;

export const journalPeerReviewModels = {
  doubleAnonymous: 'Double Anonymous',
  singleAnonymous: 'Single Anonymous',
  openPeerReview: 'Open Peer Review'
};

export type IJournalPeerReviewModel = keyof typeof journalPeerReviewModels;

type IArticleTypeCategoryId = string;
type IArticleWorkflowId = string;

type ICopyrightFormTypeOption = {
  value: string;
  mainLabel: string;
  secondaryLabel: string;
};

type ISubmissionFormTypeOption = {
  value: string;
  mainLabel: string;
  secondaryLabel: string;
  requiredField: boolean;
};

type ISubmissionFormSection = {
  mainLabel: string;
  secondaryLabel: string;
};

type IRecommendationDecision = {
  value: string;
  label: string;
};

export type ICOCConfig = {
  role: string;
  roleAbbreviation?: string;
  roleLevel?: number;
  autoAssign: boolean;
  isOwner: boolean;
  isDecisionMaker?: boolean;
  decision?: IRecommendationDecision[];
  roleMaxLimit?: number;
  maxRecommendationRequiredCount?: number;
  onlyAssignedSubmissionVisibility?: boolean;
};

/** Display abbreviation for a cocConfig entry, using flags when roleAbbreviation is unset. */
export const getCocRoleAbbreviation = (
  config: ICOCConfig,
  cocConfig: ICOCConfig[] = []
): string => {
  if (config.roleAbbreviation) {
    return config.roleAbbreviation;
  }

  const abbrevFor = (match: (entry: ICOCConfig) => boolean) =>
    cocConfig.find(entry => match(entry) && entry.roleAbbreviation)
      ?.roleAbbreviation;

  if (config.autoAssign && !config.isOwner) {
    return abbrevFor(c => c.autoAssign && !c.isOwner) ?? 'Editor';
  }
  if (config.autoAssign && config.isOwner) {
    return abbrevFor(c => c.autoAssign && !c.isOwner) ?? 'Editor';
  }
  if (!config.autoAssign && !config.isOwner) {
    return abbrevFor(c => !c.autoAssign && !c.isOwner) ?? 'Editor';
  }

  return config.role ?? '';
};

export const getCocRoleLevel = (config: ICOCConfig): number =>
  typeof config.roleLevel === 'number' ? config.roleLevel : 0;

export type ICocRecommendationRoles = {
  owner: ICOCConfig | undefined;
  recommenders: ICOCConfig[];
  firstRecommender: ICOCConfig | undefined;
  finalRecommender: ICOCConfig | undefined;
};

/** Non-owners ordered by roleLevel descending (higher number recommends first). */
export const resolveCocRecommendationRoles = (
  cocConfig: ICOCConfig[] = []
): ICocRecommendationRoles => {
  const owner = cocConfig.find(config => config.isOwner);
  const recommenders = cocConfig
    .filter(config => !config.isOwner)
    .sort((a, b) => getCocRoleLevel(b) - getCocRoleLevel(a));

  return {
    owner,
    recommenders,
    firstRecommender: recommenders[0],
    finalRecommender: recommenders[recommenders.length - 1]
  };
};

export const resolveAwaitingRecommendationRole = (
  cocConfig: ICOCConfig[],
  presentRoles: Array<string | undefined>
): string | undefined => {
  const present = new Set(
    presentRoles.filter((role): role is string => Boolean(role))
  );
  const { recommenders } = resolveCocRecommendationRoles(cocConfig);

  return recommenders.find(config => present.has(config.role))?.role;
};

type CocRecommendationParticipantRef = {
  role: string;
  status: string;
};

/** First recommender role whose required recommendation count is not yet satisfied. */
export const getCocRecommendationGateRole = (
  cocConfig: ICOCConfig[],
  participants: CocRecommendationParticipantRef[]
): string | undefined => {
  const { recommenders } = resolveCocRecommendationRoles(cocConfig);

  for (const config of recommenders) {
    const maxRequired = config.maxRecommendationRequiredCount;
    if (typeof maxRequired !== 'number' || maxRequired <= 0) {
      continue;
    }

    const roleParticipants = participants.filter(
      participant => participant.role === config.role
    );
    // Do not block later roles until this role has at least one participant in the cycle.
    if (roleParticipants.length === 0) {
      continue;
    }

    const completedCount = roleParticipants.filter(
      participant => participant.status === 'completed'
    ).length;

    if (completedCount < maxRequired) {
      return config.role;
    }
  }

  return undefined;
};

/** Active workflow role, respecting maxRecommendationRequiredCount gates. */
export const resolveRecommendationWorkflowRole = (
  cocConfig: ICOCConfig[],
  participants: CocRecommendationParticipantRef[]
): string | undefined => {
  const gateRole = getCocRecommendationGateRole(cocConfig, participants);
  if (gateRole) {
    return gateRole;
  }

  const awaitingRoles = participants
    .filter(participant => participant.status === 'awaiting')
    .map(participant => participant.role);

  return resolveAwaitingRecommendationRole(cocConfig, awaitingRoles);
};

export const getAwaitingRecommendationUserIds = (
  cocConfig: ICOCConfig[],
  participants: Array<CocRecommendationParticipantRef & { userId: string }>
): string[] =>
  participants
    .filter(participant => {
      if (participant.status !== 'awaiting') {
        return false;
      }

      const gateRole = getCocRecommendationGateRole(cocConfig, participants);
      if (gateRole) {
        return participant.role === gateRole;
      }

      return true;
    })
    .map(participant => participant.userId);

/** Defaults to required unless explicitly false (matches schema default: true). */
export function isCOCRecommendationRequired(
  isRequired: boolean | undefined
): boolean {
  return isRequired !== false;
}

/** Roles that recommend after `role` (strictly lower roleLevel). */
export const getLaterRecommenderRoles = (
  cocConfig: ICOCConfig[],
  role: string
): string[] => {
  const { recommenders } = resolveCocRecommendationRoles(cocConfig);
  const current = recommenders.find(config => config.role === role);
  if (!current) return [];

  const currentLevel = getCocRoleLevel(current);
  return recommenders
    .filter(config => getCocRoleLevel(config) < currentLevel)
    .map(config => config.role);
};

/** Every current assignee of each recommender role, in recommendation order. */
export const buildCarriedForwardRecommendationParticipants = (
  recommenderRoles: string[],
  assignees: { role: string; userId: string }[],
  assignedAt: string,
  submissionWorkflowPhase?: string
) => {
  const seen = new Set<string>();

  return recommenderRoles.flatMap(role =>
    assignees
      .filter(assignee => assignee.role === role)
      .filter(assignee => {
        const key = `${assignee.role}:${assignee.userId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(assignee => ({
        role: assignee.role,
        userId: assignee.userId,
        status: 'awaiting' as const,
        recommendation: null,
        assignedAt,
        submissionWorkflowPhase
      }))
  );
};

export type IArticleIntegrityChecks = {
  articleWorkflowId: string;
  checks: {
    checkType: ECheckType;
    runType: string;
  }[];
};

export type IIntegrityAllowedChecks = {
  role: IRoleType;
  checks: ECheckType[];
};

export type IIntegrityChecksConfig = {
  event: string;
  articleChecks?: IArticleIntegrityChecks[];
  allowedChecks?: IIntegrityAllowedChecks[];
};

export type IUADChecks = {
  checkType: EUADCheckType;
  title: string;
  isEnabled: boolean;
  category: EUADCategory;
  risk?: EUADRisk;
  configs?: {
    threshold?: {
      unit: string;
      value: string;
    };
  };
};

export type IUADChecksConfig = {
  event: string;
  checks?: IUADChecks[];
};

export type IMetadataExtraction = {
  title: string;
  description: string;
  updatedDescription: string;
  successStatusTitle: string;
  successStatusDescription: string;
  failureStatusTitle: string;
  failureStatusDescription: string;
  manualAdditionDescription: string;
  backToUploadDescription: string;
};

type IReviewSubmissionInformation = {
  header: string;
  subHeader: string;
  editTitle: string;
  editAbstract: string;
  editKeywords: string;
};

type IReviewAuthorInformation = {
  header: string;
  subHeader: string;
  editAuthorInformation: ISubmissionFormSection;
  submissionManagementInformation: ISubmissionFormSection;
  potentialFundingRestrictions: ISubmissionFormSection;
  coAuthorConsent: ISubmissionFormSection;
  editCorrespondingAuthor: ISubmissionFormSection;
};

type ISubmissionPreferences = {
  header: string;
  subHeader: string;
  dataAvailability: ISubmissionFormSection;
  researchDataNotPubliclyAvailable: ISubmissionFormSection;
  motivationToPublish: ISubmissionFormSection;
  licensesAndPermissions: ISubmissionFormSection;
  peerReviewPreferences: ISubmissionFormSection;
  ethics: ISubmissionFormSection;
  funding: ISubmissionFormSection;
  fundingGeneric: ISubmissionFormSection;
  figuresAndTables: ISubmissionFormSection;
  additionalPreferences: ISubmissionFormSection;
  publishingModel: ISubmissionFormSection;
  consentToPublication: ISubmissionFormSection;
};
export type ILexiconField = {
  predicateLabel: string;
  label: string;
  tooltip?: string;
  advancedSearch: { autosuggest: boolean; multiSelect?: boolean } | false;
  facet: boolean;
  reviewerCard: boolean;
  actionCard: boolean;
  required?: boolean;
  controlType?: 'input' | 'tag' | 'textarea';
  fieldsForm?: boolean;
};

export type ILexicon = {
  fields: ILexiconField[];
  dateFilters: { key: string; value: string[]; label: string }[];
  userFilter: {
    key: string;
    label: string;
  }[];
  reviewerActionCard: {
    createAccount: boolean;
  };
};

export type IContentManagementSystem = {
  copyrightPublicationTerms: string;
  authorSubmissionForm: {
    startSubmissionForm: {
      header: string;
      subHeader: string;
      formFields: ISubmissionFormTypeOption[];
    };
    metadataExtraction: IMetadataExtraction;
    reviewSubmissionInformation: IReviewSubmissionInformation;
    reviewAuthorInformation: IReviewAuthorInformation;
    submissionPreferences: ISubmissionPreferences;
  };
  editorialViewForm?: IeditorialFormData;
  lexicon: ILexicon;
};

export type IeditorialFormData = {
  reviews: {
    inviteReviewersTooltip: string;
    addReviewersToQueue: string;
  };
};

export type exportServerInfo = {
  host: string;
  port: number;
  username: string;
  password: string;
  path: string;
};
export type s3ServerInfo = {
  bucket: string;
  region: string;
  path: string;
  access_key: string;
  secret_key: string;
};
export type gcsServerInfo = {
  bucket: string;
  private_key: string;
  path: string;
  project_id: string;
  client_email: string;
};
export type apiServerInfo = {
  endpoint: string;
  shouldPush: boolean;
};
export type exportConfigItem = {
  id: string;
  name: string;
  exportType: string;
  exportConfigType: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  serverInfo:
    | exportServerInfo
    | s3ServerInfo
    | gcsServerInfo
    | apiServerInfo
    | {};
  isActive?: boolean;
};

export type IRequiredUserProfileData = {
  fieldName: string;
  fieldType: string;
};

export type IProfileNotesVisibility = 'global' | 'journal';

export type IUserProfileConfig = {
  requiredUserProfileData: IRequiredUserProfileData[];
  profileNotesVisibility?: IProfileNotesVisibility;
};

export type IJournalEmailAddress = {
  email: string;
  name?: string;
};

export type IJournalEmailsConfig = {
  additionalEmails?: IJournalEmailAddress[];
  /** Omitted when the journal has no sender override of its own. */
  fromEmail?: IJournalEmailAddress;
  /** The journal's support address, used as the reply-to on its mail. */
  replyTo: IJournalEmailAddress[];
};

export type IPeerReviewModelConfig = {
  articleWorkflowId: IArticleWorkflowId;
  model: IJournalPeerReviewModel;
};

export type ISubmissionContextConfig = {
  email?: string;
  url?: string;
  issn?: {
    print?: string;
    online?: string;
  };
  publishingModel: IJournalPublishingModel;
  peerReviewModel: IPeerReviewModelConfig[];
  openAccessCopyrightOptions: string[];
  subscriptionCopyrightOptions: string[];
  openAccessLicenseOptions: string[];
  openAccessWaiverReasonOptions: string[];
  editorialWithdrawalOptions: string[];
  authorWithdrawalOptions: string[];
  revisionSeverityOptions: string[];
  openAccessChoices: string[];
  transferOptions: string[];
  rejectionOptions: string[];
  openAccessCopyrightFormTypeOptions: ICopyrightFormTypeOption[];
  subscriptionCopyrightFormTypeOptions: ICopyrightFormTypeOption[];
  cms: IContentManagementSystem;
  globalLexiconConfig?: boolean;
  reviewerSearch?: boolean;
  assignReviewers?: boolean;
  lexiconProjectId?: string;
  lexiconClassId?: string;
  publonsJournalId?: string;
  isCoReviewEnabled?: boolean;
  reviewGuidelineLink?: string;
  reviewInformationLink?: string;
  scopeLink?: string;
  authorCopyrightModel?: string;
  authorCopyrightFormUrl?: string;
  authorICMJEModel?: string;
  authorICMJEFormUrl?: string;
  decisionMenuConfig?: Partial<IDecisionMenuConfig>;
  isCOCRecommendationRequired?: boolean;
  isAdjudicationEnabled?: boolean;
  isEarlyAssessmentEnabled?: boolean;
  cocConfig?: ICOCConfig[];
  integrityChecks?: IIntegrityChecksConfig[];
  authorRequirements?: Partial<Record<IAuthorRequirementOptions, boolean>>;
  logoUrl?: string;
  fileConversionFields?: string[];
  exportConfig?: exportConfigItem[];
  reviewerAcountCreation?: boolean;
  exportXMLFormat?: string;
  uadChecks?: IUADChecksConfig[];
  userProfileConfig?: IUserProfileConfig;
  isUADEnable?: boolean;
  uadReportTimeWindowHours?: number;
  reviewerRating?: boolean;
  boardMemberReviewerAssigment?: boolean;
  reviewerRecommendationProviders?: IReviewerRecommendationProvider[];
  enableSuggestKeywords?: boolean;
  enableConsolidatedPdf?: boolean;
  activationDate?: Date | string;
  journalEmails?: IJournalEmailsConfig;
};

/**
 * Resolves the peer review model for a single article workflow.
 *
 * `config.peerReviewModel` is being migrated from a single per-journal string to a
 * per-article-workflow list. Both shapes can therefore appear in existing documents
 * until every environment has run the migration, so the legacy string is tolerated here.
 */
export const getPeerReviewModelForWorkflow = (
  config: Pick<ISubmissionContextConfig, 'peerReviewModel'> | undefined,
  articleWorkflowId: string | undefined
): IJournalPeerReviewModel => {
  const value = config?.peerReviewModel as unknown;

  if (typeof value === 'string') {
    return value as IJournalPeerReviewModel;
  }

  const entries = Array.isArray(value)
    ? (value as IPeerReviewModelConfig[])
    : [];

  return (
    (articleWorkflowId &&
      entries.find(entry => entry.articleWorkflowId === articleWorkflowId)
        ?.model) ||
    'doubleAnonymous'
  );
};

/**
 * Form fields that are valid only for a given peer review model. `BlindedReviewConsent`
 * asks an author to confirm a blinded manuscript, so it belongs to double-anonymous
 * review alone.
 *
 * Consumers use these names to add or drop the fields from an article workflow's
 * authorSubmission schema when its model is configured or changed.
 */
export const journalPeerReviewModelFormFields: Record<
  IJournalPeerReviewModel,
  string[]
> = {
  doubleAnonymous: ['BlindedReviewConsent'],
  singleAnonymous: [],
  openPeerReview: []
};

/** Every field name managed across all peer review models. */
export const allJournalPeerReviewModelFormFields = Object.values(
  journalPeerReviewModelFormFields
).flat();

export enum ExtractionType {
  OnlyManuscript = 'OnlyManuscript',
  TitleAndManuscript = 'TitleAndManuscript'
}

export type ISubmissionContext = {
  id: string;
  organizationId: string;
  workspaceId: string;
  portfolioId: string;
  isActive: boolean;
  isBaseJournal?: boolean;
  extractionType: string;
  type: ISubmissionContextType;
  data: {
    name: string;
    shortname: string;
    description?: string;
    introduction?: string;
    organisation?: string;
    focusIssues?: {
      id: string;
      name: string;
      issueId: string;
      issueLink?: string;
      status: string;
    }[];
    guidelineLink?: string;
  };
  articleWorkflows: Record<IArticleTypeCategoryId, IArticleWorkflowId>;
  config: ISubmissionContextConfig;
  transferNetworkIds?: string[];
  boardMembers?: string[];
  created_at?: Date | string;
  modified_at?: Date | string;
  articleTypes?: {
    articleTypeCategoryId: string;
    articleWorkflowId: string;
    name: string;
    description: string;
    formSchemas: {
      authorSubmission: string;
      editorRevisionRequest: string;
      authorRevision: string;
      reviewerFirstAssessment: string;
      reviewerRevisionAssessment: string;
    };
  }[];
};

export type IBoardMemberItem = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
};

export type IBoardMemberList = {
  users: IBoardMemberItem;
};

export type IJournalWiseUserRoleList = {
  id: string;
  name: string;
  shortname: string;
  roles: string[];
};
